import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'sonner'
import PortalShell from './layouts/PortalShell'
import AuthLayout from './layouts/AuthLayout'
import LoadingScreen from './components/shared/LoadingScreen'
import { useAuthStore } from './stores/authStore'

const HomePage = lazy(() => import('./pages/HomePage'))
const DevicesPage = lazy(() => import('./pages/devices/DevicesPage'))
const DeviceDetailPage = lazy(() => import('./pages/devices/DeviceDetailPage'))
const PLCStatusPage = lazy(() => import('./pages/devices/PLCStatusPage'))
const ProposalsPage = lazy(() => import('./pages/proposals/ProposalsPage'))
const NewProposalPage = lazy(() => import('./pages/proposals/NewProposalPage'))
const ProposalViewPage = lazy(() => import('./pages/proposals/ProposalViewPage'))
const CompareProposalsPage = lazy(() => import('./pages/proposals/CompareProposalsPage'))
const AlertsPage = lazy(() => import('./pages/alerts/AlertsPage'))
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'))
const UsersPage = lazy(() => import('./pages/admin/UsersPage'))
const InvitationsPage = lazy(() => import('./pages/admin/InvitationsPage'))
const ApiKeysPage = lazy(() => import('./pages/admin/ApiKeysPage'))
const IntegrationsPage = lazy(() => import('./pages/admin/IntegrationsPage'))
const MonitoringTierPage = lazy(() => import('./pages/admin/MonitoringTierPage'))
const WebhooksPage = lazy(() => import('./pages/admin/WebhooksPage'))
const DeveloperPage = lazy(() => import('./pages/admin/DeveloperPage'))
const SitesPage = lazy(() => import('./pages/admin/SitesPage'))
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'))
const LoginPage = lazy(() => import('./pages/auth/LoginPage'))
const InvitePage = lazy(() => import('./pages/auth/InvitePage'))
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'))
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'))
const TermsPage = lazy(() => import('./pages/legal/TermsPage'))
const PrivacyPage = lazy(() => import('./pages/legal/PrivacyPage'))
const SupportPage = lazy(() => import('./pages/legal/SupportPage'))
const SupportTicketsPage = lazy(() => import('./pages/support/SupportTicketsPage'))
const VPPFleetsPage = lazy(() => import('./pages/vpp/VPPFleetsPage'))
const FleetDetailPage = lazy(() => import('./pages/vpp/FleetDetailPage'))
const ConsumerShell = lazy(() => import('./layouts/ConsumerShell'))
const ConsumerHomePage = lazy(() => import('./pages/consumer/ConsumerHomePage'))
const MySystemPage = lazy(() => import('./pages/consumer/MySystemPage'))

export default function App() {
  const user = useAuthStore((s) => s.user)
  const isCustomer = user?.role === 'customer'

  return (
    <>
      <Toaster
        theme="dark"
        position="top-right"
        toastOptions={{
          style: {
            background: '#1a1a2e',
            border: '1px solid #2a2a4a',
            color: '#e0e0f0',
          },
        }}
      />
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          {/* Auth pages -- full screen, no shell */}
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/invite" element={<InvitePage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          </Route>

          {/* Legal pages -- full screen, no shell */}
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/support" element={<SupportPage />} />

          {isCustomer ? (
            <>
              {/* Consumer portal shell */}
              <Route element={<ConsumerShell />}>
                <Route index element={<ConsumerHomePage />} />
                <Route path="/my-system" element={<MySystemPage />} />
                <Route path="/devices/:id" element={<DeviceDetailPage />} />
                <Route path="/alerts" element={<AlertsPage />} />
                <Route path="/support-tickets" element={<SupportTicketsPage />} />
                <Route path="/settings" element={<SettingsPage />} />
              </Route>
            </>
          ) : (
            <>
              {/* Installer portal shell -- sidebar + header */}
              <Route element={<PortalShell />}>
                <Route index element={<HomePage />} />
                <Route path="/devices" element={<DevicesPage />} />
                <Route path="/devices/:id" element={<DeviceDetailPage />} />
                <Route path="/devices/:id/plc" element={<PLCStatusPage />} />
                <Route path="/proposals" element={<ProposalsPage />} />
                <Route path="/proposals/new" element={<NewProposalPage />} />
                <Route path="/proposals/compare" element={<CompareProposalsPage />} />
                <Route path="/proposals/:id" element={<ProposalViewPage />} />
                <Route path="/alerts" element={<AlertsPage />} />
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<Navigate to="users" replace />} />
                  <Route path="users" element={<UsersPage />} />
                  <Route path="invitations" element={<InvitationsPage />} />
                  <Route path="api-keys" element={<ApiKeysPage />} />
                  <Route path="integrations" element={<IntegrationsPage />} />
                  <Route path="monitoring" element={<MonitoringTierPage />} />
                  <Route path="sites" element={<SitesPage />} />
                  <Route path="webhooks" element={<WebhooksPage />} />
                  <Route path="developer" element={<DeveloperPage />} />
                </Route>
                <Route path="/vpp" element={<VPPFleetsPage />} />
                <Route path="/vpp/:id" element={<FleetDetailPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/support-tickets" element={<SupportTicketsPage />} />
              </Route>

              {/* Backward compat redirects */}
              <Route path="/noc" element={<Navigate to="/devices" replace />} />
              <Route path="/noc/*" element={<Navigate to="/devices" replace />} />
              <Route path="/proposal" element={<Navigate to="/proposals/new" replace />} />
              <Route path="/proposal/*" element={<Navigate to="/proposals/new" replace />} />
            </>
          )}

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </>
  )
}
